import { type InstagramBasicCredentials, type InstagramCookieCredentials } from '@unipile/provider-instagram';
import { type ProviderAuthenticationIntentResponse } from '@unipile/types';
import {
  CancelButton,
  UniLoginForm,
  ScopesConsentScreen,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useScopesConsent,
  useStartProviderAuthIntent,
  useHostedAuthSession,
} from '@unipile/ui';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CookiesForm } from './CookiesForm';
import { CredentialsForm } from './CredentialsForm';

type Step = 'login' | 'consent';

export function LoginScreen({
  onSuccess,
  onError,
  onCancel,
}: {
  onSuccess: (data: ProviderAuthenticationIntentResponse) => void;
  onError: (error: unknown) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const { data: session } = useHostedAuthSession();
  const allowMethods = session?.config?.instagram?.allow_methods ?? ['credentials', 'unilogin'];

  // Scopes consent management
  const { scopes, hasScopes, brandName, cancelConsent } = useScopesConsent({ provider: 'instagram' });

  const [step, setStep] = useState<Step>('login');
  const [credentials, setCredentials] = useState<InstagramBasicCredentials | InstagramCookieCredentials>();

  const { isPending, mutate } = useStartProviderAuthIntent({
    mutationConfig: {
      onSuccess,
      onError: (error) => {
        if (error instanceof AxiosError) {
          const data = error.response?.data;
          if (data && typeof data === 'object' && 'type' in data && data.type === 'provider/invalid_credentials') {
            setStep('login');
          }
        }
        onError(error);
      },
    },
  });

  const onSubmit = (submitted: InstagramBasicCredentials | InstagramCookieCredentials) => {
    const credentials = { ...submitted, user_agent: navigator.userAgent };

    if (hasScopes) {
      setCredentials(credentials);
      setStep('consent');
    } else {
      mutate({ data: { provider: 'instagram', credentials } });
    }
  };

  // Step: Scopes consent screen
  if (step === 'consent' && credentials) {
    return (
      <ScopesConsentScreen
        scopes={scopes}
        brandName={brandName}
        onAccept={() => {
          if (!credentials) return;
          mutate({ data: { provider: 'instagram', credentials } });
        }}
        onCancel={() => {
          cancelConsent();
          setStep('login');
        }}
        isPending={isPending}
      />
    );
  }

  // Step: Login
  return (
    <>
      <Tabs
        defaultValue={
          allowMethods.includes('credentials') ? 'credentials' : allowMethods.includes('cookies') ? 'cookies' : 'unilogin'
        }
      >
        {allowMethods.length > 1 && (
          <TabsList className="w-full">
            {allowMethods.includes('credentials') && (
              <TabsTrigger className="w-full" value="credentials">
                {t('LoginScreen.method.credentials', { ns: 'instagram' })}
              </TabsTrigger>
            )}

            {allowMethods.includes('cookies') && (
              <TabsTrigger className="w-full" value="cookies">
                {t('LoginScreen.method.cookies', { ns: 'instagram' })}
              </TabsTrigger>
            )}
            {allowMethods.includes('unilogin') && (
              <TabsTrigger className="w-full" value="unilogin">
                {session?.unilogin_tab_name || 'UniLogin'}
              </TabsTrigger>
            )}
          </TabsList>
        )}
        {allowMethods.includes('unilogin') && (
          <TabsContent value="unilogin">
            <UniLoginForm provider="instagram" onSuccess={onSuccess} onError={onError} onCancel={onCancel} />
          </TabsContent>
        )}
        <TabsContent value="credentials">
          <CredentialsForm onSubmit={onSubmit} isPending={isPending && !hasScopes} />
          <CancelButton onCancel={onCancel} disabled={isPending} />
        </TabsContent>
        <TabsContent value="cookies">
          <CookiesForm onSubmit={onSubmit} isPending={isPending && !hasScopes} />
          <CancelButton onCancel={onCancel} disabled={isPending} />
        </TabsContent>
      </Tabs>
    </>
  );
}
